import { NgZone } from '@angular/core';
import mapboxgl from 'mapbox-gl';

/**
 * Controlador de seguimiento de cámara para Mapbox
 * Responsabilidad única: seguir un punto objetivo en el mapa de forma suave
 * Completamente independiente del marker que representa ese punto
 */
export class CameraFollowController {
  // Estado del loop de seguimiento
  private followFrame?: number;
  private isPaused: boolean = false;
  private resumeTimeout?: number;
  private isUpdatingCamera: boolean = false; // Flag para ignorar eventos propios

  // Listeners de eventos
  private zoomStartListener?: () => void;
  private zoomEndListener?: () => void;
  private pitchStartListener?: () => void;
  private pitchEndListener?: () => void;

  // Estado
  private isActive: boolean = false;
  private isDragBlocked: boolean = false;

  constructor(
    private map: mapboxgl.Map,
    private getTargetPosition: () => { lng: number; lat: number },
    private ngZone: NgZone,
    private onPositionUpdate?: () => void,
  ) {}

  /**
   * Inicia el seguimiento de cámara
   */
  start(): void {
    if (this.isActive) {
      return;
    }

    this.isActive = true;

    // Bloquear solo drag/pan (zoom y pitch quedan libres)
    this.blockDrag();

    // Setup listeners para pausar/reanudar
    this.setupInteractionListeners();

    // Iniciar loop de seguimiento
    this.startLoop();
  }

  /**
   * Detiene el seguimiento de cámara completamente
   */
  stop(): void {
    if (!this.isActive) {
      return;
    }

    this.isActive = false;

    // Detener loop
    this.stopLoop();

    // Limpiar listeners
    this.cleanupInteractionListeners();

    // Cancelar timeout de reanudación
    this.cancelResumeTimeout();

    // Desbloquear drag
    this.unblockDrag();

    // Reset flags
    this.isPaused = false;
  }

  /**
   * Pausa el seguimiento temporalmente (sin desbloquear drag)
   */
  pause(): void {
    this.isPaused = true;
    this.cancelResumeTimeout();
  }

  /**
   * Reanuda el seguimiento después de una pausa
   */
  resume(): void {
    this.isPaused = false;
  }

  /**
   * Verifica si el seguimiento está activo
   */
  isFollowing(): boolean {
    return this.isActive;
  }

  /**
   * Destruye el controlador y limpia recursos
   */
  destroy(): void {
    this.stop();
  }

  // ========================================
  // MÉTODOS PRIVADOS
  // ========================================

  /**
   * Bloquea el drag/pan del mapa
   */
  private blockDrag(): void {
    if (!this.isDragBlocked && this.map.dragPan.isEnabled()) {
      this.map.dragPan.disable();
      this.isDragBlocked = true;
    }
  }

  /**
   * Desbloquea el drag/pan del mapa
   */
  private unblockDrag(): void {
    if (this.isDragBlocked && !this.map.dragPan.isEnabled()) {
      this.map.dragPan.enable();
      this.isDragBlocked = false;
    }
  }

  /**
   * Configura listeners para detectar interacción del usuario
   * IMPORTANTE: Ignora eventos causados por nuestros propios setCenter()
   */
  private setupInteractionListeners(): void {
    // Zoom start → pausar SOLO si no somos nosotros
    this.zoomStartListener = () => {
      if (this.isUpdatingCamera) {
        // Ignorar - es nuestro propio setCenter()
        return;
      }
      this.pause();
    };

    // Zoom end → programar reanudación SOLO si no somos nosotros
    this.zoomEndListener = () => {
      if (this.isUpdatingCamera) {
        // Ignorar - es nuestro propio setCenter()
        return;
      }
      this.scheduleResume();
    };

    // Pitch start → pausar SOLO si no somos nosotros
    this.pitchStartListener = () => {
      if (this.isUpdatingCamera) {
        // Ignorar - es nuestro propio setCenter()
        return;
      }
      this.pause();
    };

    // Pitch end → programar reanudación SOLO si no somos nosotros
    this.pitchEndListener = () => {
      if (this.isUpdatingCamera) {
        // Ignorar - es nuestro propio setCenter()
        return;
      }
      this.scheduleResume();
    };

    this.map.on('zoomstart', this.zoomStartListener);
    this.map.on('zoomend', this.zoomEndListener);
    this.map.on('pitchstart', this.pitchStartListener);
    this.map.on('pitchend', this.pitchEndListener);
  }

  /**
   * Limpia listeners de interacción
   */
  private cleanupInteractionListeners(): void {
    if (this.zoomStartListener) {
      this.map.off('zoomstart', this.zoomStartListener);
      this.zoomStartListener = undefined;
    }
    if (this.zoomEndListener) {
      this.map.off('zoomend', this.zoomEndListener);
      this.zoomEndListener = undefined;
    }
    if (this.pitchStartListener) {
      this.map.off('pitchstart', this.pitchStartListener);
      this.pitchStartListener = undefined;
    }
    if (this.pitchEndListener) {
      this.map.off('pitchend', this.pitchEndListener);
      this.pitchEndListener = undefined;
    }
  }

  /**
   * Programa la reanudación del seguimiento después de 500ms
   */
  private scheduleResume(): void {
    this.cancelResumeTimeout();
    this.resumeTimeout = window.setTimeout(() => {
      this.resume();
      this.resumeTimeout = undefined;
    }, 500);
  }

  /**
   * Cancela el timeout de reanudación si existe
   */
  private cancelResumeTimeout(): void {
    if (this.resumeTimeout !== undefined) {
      clearTimeout(this.resumeTimeout);
      this.resumeTimeout = undefined;
    }
  }

  /**
   * Inicia el loop de seguimiento a 60 FPS con interpolación manual
   * Estrategia: Interpolar suavemente hacia la posición del marker sin usar animaciones de Mapbox
   * Esto evita conflictos con zoom/pitch del usuario y da control total del movimiento
   */
  private startLoop(): void {
    const followLoop = () => {
      // Si se detuvo el seguimiento, salir
      if (!this.isActive || this.followFrame === undefined) {
        return;
      }

      // Solo actualizar si no está pausado
      if (!this.isPaused) {
        // Obtener posición objetivo (posición actual del marker)
        const target = this.getTargetPosition();

        // Obtener centro actual de la cámara
        const currentCenter = this.map.getCenter();

        // Calcular diferencia
        const diffLng = target.lng - currentCenter.lng;
        const diffLat = target.lat - currentCenter.lat;

        // Solo actualizar si hay diferencia significativa (> ~50cm)
        if (Math.abs(diffLng) > 0.000005 || Math.abs(diffLat) > 0.000005) {
          // Interpolación suave: mover un % de la distancia hacia el target en cada frame
          // Factor de 0.3 = seguimiento rápido y responsive sin nerviosismo
          // Factor más alto (ej. 0.5) = más inmediato pero puede ser nervioso
          // Factor más bajo (ej. 0.1) = más suave pero puede parecer lento
          const lerpFactor = 0.3;

          const newLng = currentCenter.lng + diffLng * lerpFactor;
          const newLat = currentCenter.lat + diffLat * lerpFactor;

          // Marcar que estamos actualizando para ignorar eventos propios
          this.isUpdatingCamera = true;

          // Usar setCenter() que solo mueve el centro sin tocar zoom/pitch
          // Esto respeta completamente el zoom y pitch del usuario
          this.map.setCenter([newLng, newLat]);

          // Desmarcar inmediatamente
          this.isUpdatingCamera = false;

          // Notificar actualización de posición para change detection (throttled en el componente)
          if (this.onPositionUpdate) {
            this.onPositionUpdate();
          }
        }
      }

      // Continuar el loop (60 FPS)
      this.followFrame = this.ngZone.runOutsideAngular(() => {
        return requestAnimationFrame(followLoop);
      });
    };

    // Iniciar el loop
    this.followFrame = this.ngZone.runOutsideAngular(() => {
      return requestAnimationFrame(followLoop);
    });
  }

  /**
   * Detiene el loop de seguimiento
   */
  private stopLoop(): void {
    if (this.followFrame !== undefined) {
      cancelAnimationFrame(this.followFrame);
      this.followFrame = undefined;
    }
  }
}
